import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useStorageListener from '~/hooks/browser/useStorageListener';
import useTabVisibility from '~/hooks/browser/useTabVisibility';
import useIsOnline from '~/hooks/network/useIsOnline';
import { getAccessToken } from '~/services/localStorage/auth';
import connectionCloseDelay from '~/services/socket/helpers/connectionCloseDelay';
import getOrCreateSignalRTabId from '~/services/socket/helpers/getSignalRTabId';
import useStableRetryReset from '~/services/socket/hooks/useStableRetryReset';
import shouldRecoverSignalR from '~/services/socket/helpers/shouldRecoverSignalR';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

interface ISignalRTokenContext {
  token: string | null;
  isOnline: boolean;
  handleError: (error?: Error) => Promise<void>;
  handleConnection: () => void;
}

const SignalRTokenContext = createContext<ISignalRTokenContext | null>(null);

interface IProps {
  children: ReactNode | ReactNode[];
}

const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';

export const SignalRTokenProvider = ({ children }: IProps): ReactElement => {
  const [token, setToken] = useState<string | null>(null);
  const { current } = useStoreState(state => state.user);
  const isOnline = useIsOnline();
  const isTabActive = useTabVisibility();
  const tabIdRef = useRef<string>(getOrCreateSignalRTabId());
  const wasTabActiveRef = useRef<boolean>(isTabActive);
  const isRecoveringRef = useRef<boolean>(false);
  const triesRef = useRef<number>(0);
  const handleConnection = useStableRetryReset(triesRef);

  const { onGetCurrentProfile } = useStoreActions(state => state.user);
  const showError = useStoreActions(actions => actions.snackbar.showError);

  // eslint-disable-next-line consistent-return
  const handleError = async () => {
    const shouldSkipRecovery = triesRef.current >= 5 || !isOnline || isRecoveringRef.current;

    if (shouldSkipRecovery) {
      return connectionCloseDelay();
    }
    // Trigger Axios interceptor to refresh the token upon encountering a connection error.
    // The error can stem from various issues, such as a loss of internet connectivity, server unreachability, or a SignalR service outage.
    // A maximum of 5 retry attempts will be made. The retry counter will be reset upon a successful reconnect,
    // restoration of network connectivity, or when the tab becomes visible again.
    // If the token refresh process fails, the user will be logged out by the Axios interceptor.
    if (!shouldRecoverSignalR(tabIdRef.current)) {
      return connectionCloseDelay();
    }

    try {
      isRecoveringRef.current = true;
      await onGetCurrentProfile();
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      isRecoveringRef.current = false;
      triesRef.current += 1;
      setToken(null);
    }
  };

  useEffect(() => {
    const accessToken = getAccessToken();

    if (accessToken && !token && current) {
      setToken(accessToken);
    } else if (!accessToken) {
      setToken(null);
    }
  }, [token, current]);

  useEffect(() => {
    if (isOnline) {
      triesRef.current = 0;
    }
  }, [isOnline]);

  // returning to the tab is treated as a recovery signal
  // providers depending on token get a reconnection cycle
  useEffect(() => {
    if (isTabActive && !wasTabActiveRef.current && current && getAccessToken()) {
      triesRef.current = 0;
      setToken(null);
    }

    wasTabActiveRef.current = isTabActive;
  }, [current, isTabActive]);

  const handleTokenStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key !== ACCESS_TOKEN_STORAGE_KEY || !current || !isOnline) {
        return;
      }

      triesRef.current = 0;
      setToken(null);
    },
    [current, isOnline],
  );

  useStorageListener(handleTokenStorageChange);

  const contextValue: ISignalRTokenContext = {
    token,
    isOnline,
    handleError,
    handleConnection,
  };

  return (
    <SignalRTokenContext.Provider value={contextValue}>{children}</SignalRTokenContext.Provider>
  );
};

export const useSignalRToken = (): ISignalRTokenContext => {
  const context = useContext(SignalRTokenContext);
  if (!context) {
    throw new Error('useSignalRToken must be used within a SignalRTokenProvider');
  }
  return context;
};
